import { USER_MGMT_CNST } from '../../../../constants/proj.cnst';
import { IDepartmentOption, IParsedUserDetails } from '../../../../interfaces/list-users.config';

const PLACEHOLDER = USER_MGMT_CNST.USER_SUMMARY_PLACEHOLDER;
const DEPARTMENT_KEY = USER_MGMT_CNST.USER_DETAILS_JSON_KEYS.DEPARTMENT;
const DIVISION_KEY = USER_MGMT_CNST.USER_DETAILS_JSON_KEYS.DIVISION;

export function parseUserDetailsJson(userDetailsJson: unknown): IParsedUserDetails {
  const parsedDetails = toUserDetailsObject(userDetailsJson);

  return {
    department: normalizeDetailValue(resolveDetailFieldValue(parsedDetails, DEPARTMENT_KEY, ['dept'])),
    division: normalizeDetailValue(resolveDetailFieldValue(parsedDetails, DIVISION_KEY, ['div']))
  };
}

export function mapUserWithDetails(user: any): any {
  const parsedDetails = parseUserDetailsJson(user?.userDetailsJson);

  return {
    ...user,
    department: resolveDetailValue(parsedDetails.department, user?.department),
    division: resolveDetailValue(parsedDetails.division, user?.division)
  };
}

export function mapUsersWithDetails(users: any[]): any[] {
  if (!Array.isArray(users)) {
    return [];
  }

  return users.map((user) => mapUserWithDetails(user));
}

export function buildDepartmentOptions(users: any[]): IDepartmentOption[] {
  return buildUniqueDetailOptions(users, DEPARTMENT_KEY);
}

export function buildUniqueDetailOptions(users: any[], detailKey: string): IDepartmentOption[] {
  const uniqueValues = Array.from(new Set(
    (Array.isArray(users) ? users : [])
      .map((user) => user?.[detailKey])
      .filter((value) => hasDetailValue(value))
      .map((value) => coerceDetailToString(value))
  )).sort((first, second) => first.localeCompare(second));

  return uniqueValues.map((value) => ({
    label: value,
    value
  }));
}

export function addUniqueDetailOption(options: IDepartmentOption[], value: unknown): IDepartmentOption[] {
  const normalizedValue = coerceDetailToString(value);
  if (!normalizedValue || !Array.isArray(options)) {
    return Array.isArray(options) ? options : [];
  }

  const hasExistingOption = options.some(
    (option) => option?.value?.toLowerCase() === normalizedValue.toLowerCase()
  );
  if (hasExistingOption) {
    return options;
  }

  return [...options, { label: normalizedValue, value: normalizedValue }]
    .sort((first, second) => first.label.localeCompare(second.label));
}

export function getFormDetailValue(value: unknown): string {
  return hasDetailValue(value) ? coerceDetailToString(value) : '';
}

export function resolveUserDetailsForForm(user: any): IParsedUserDetails {
  const mappedUser = mapUserWithDetails(user);

  return {
    department: getFormDetailValue(mappedUser.department),
    division: getFormDetailValue(mappedUser.division)
  };
}

export function serializeUserDetailsJson(department: unknown, division: unknown): {} {
  return {
    data: {
      [DEPARTMENT_KEY]: coerceDetailToString(department),
      [DIVISION_KEY]: coerceDetailToString(division)
    }
  };
}

export function normalizeUserListResponse(response: unknown): any[] {
  if (response == null) {
    return [];
  }

  const payload = Array.isArray(response)
    ? response
    : (typeof response === 'object' && 'response' in (response as object)
      ? (response as { response: unknown }).response
      : response);

  if (payload == null || typeof payload !== 'object') {
    return [];
  }

  return Object.keys(payload).map((userId) => ({
    userId,
    ...(payload as Record<string, any>)[userId]
  }));
}

function toUserDetailsObject(userDetailsJson: unknown): Record<string, unknown> | null {
  return toDetailsObject(userDetailsJson, 0);
}

function unwrapDetailsData(parsedDetails: Record<string, unknown>): unknown {
  const nestedData = parsedDetails['data'];
  if (nestedData != null) {
    return nestedData;
  }

  return parsedDetails;
}

function toDetailsObject(value: unknown, depth: number): Record<string, unknown> | null {
  if (depth > 3 || value == null) {
    return null;
  }

  if (typeof value === 'object' && !Array.isArray(value)) {
    const unwrappedData = unwrapDetailsData(value as Record<string, unknown>);
    if (unwrappedData && typeof unwrappedData === 'object' && !Array.isArray(unwrappedData)) {
      return unwrappedData as Record<string, unknown>;
    }
    if (typeof unwrappedData === 'string') {
      return toDetailsObject(unwrappedData, depth + 1);
    }
    return null;
  }

  if (typeof value !== 'string') {
    return null;
  }

  const trimmedJson = value.trim();
  if (!trimmedJson) {
    return null;
  }

  try {
    return toDetailsObject(JSON.parse(trimmedJson), depth + 1);
  } catch {
    return null;
  }
}

function resolveDetailFieldValue(parsedDetails: Record<string, unknown> | null, detailKey: string, fallbackKeys: string[] = []): unknown {
  if (!parsedDetails) {
    return null;
  }

  const keysToCheck = [detailKey, ...fallbackKeys];
  for (const key of keysToCheck) {
    const value = getValueByKey(parsedDetails, key);
    if (value != null) {
      return value;
    }
  }

  return null;
}

function getValueByKey(source: Record<string, unknown>, key: string): unknown {
  if (Object.prototype.hasOwnProperty.call(source, key)) {
    return source[key];
  }

  const normalizedTargetKey = normalizeDetailKey(key);
  const matchedSourceKey = Object.keys(source).find(
    (sourceKey) => normalizeDetailKey(sourceKey) === normalizedTargetKey
  );

  return matchedSourceKey ? source[matchedSourceKey] : null;
}

function normalizeDetailKey(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]/g, '');
}

function resolveDetailValue(parsedValue: string, fallbackValue: unknown): string {
  if (hasDetailValue(parsedValue)) {
    return parsedValue;
  }

  return normalizeDetailValue(fallbackValue);
}

function normalizeDetailValue(value: unknown): string {
  const normalizedValue = coerceDetailToString(value);
  return normalizedValue ? normalizedValue : PLACEHOLDER;
}

function hasDetailValue(value: unknown): boolean {
  const normalizedValue = coerceDetailToString(value);
  return !!normalizedValue && normalizedValue !== PLACEHOLDER;
}

function coerceDetailToString(value: unknown): string {
  if (value === null || value === undefined) {
    return '';
  }

  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value).trim();
  }

  if (typeof value === 'object' && !Array.isArray(value)) {
    const objectValue = value as Record<string, unknown>;
    const candidate = objectValue['value'] ?? objectValue['label'] ?? objectValue['name'];
    return candidate == null ? '' : String(candidate).trim();
  }

  return '';
}
